import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { categorySlug, eventDescription } from "@/components/EventCard";

const CATEGORIES = [
  ["all", "All"], ["on-this-day", "On This Day"], ["births", "Births"], ["deaths", "Deaths"], ["global-events", "Global Events"],
  ["song-releases", "Songs"], ["film-releases", "Films"], ["inventions", "Inventions"], ["discoveries", "Discoveries"],
];
const LIMIT = 30;

const Chip = ({ label, meta, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] bg-white border border-[#e5e7eb] text-ink hover:border-gold hover:text-gold transition-colors">
    {label}{meta != null && <span className="text-[11px] text-[#9ca3af]">{meta}</span>}
  </button>
);

const SearchChips = ({ suggest, onPick, compact = false }) => (
  <div data-testid="search-suggestions" className={compact ? "mt-5 flex flex-wrap justify-center gap-2" : "mt-5 space-y-3"}>
    {!compact && suggest.popular.length > 0 && (
      <div className="flex flex-wrap items-center gap-2" data-testid="popular-searches">
        <span className="text-[11px] uppercase tracking-widest text-[#9ca3af] mr-1">Popular</span>
        {suggest.popular.map((p) => <Chip key={p.term} label={p.term} meta={p.results} onClick={() => onPick(p.term)} testid={`popular-chip-${p.term.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>
    )}
    <div className={compact ? "contents" : "flex flex-wrap items-center gap-2"} data-testid="try-searches">
      <span className="text-[11px] uppercase tracking-widest text-[#9ca3af] mr-1">Try</span>
      {suggest.try.map((t) => <Chip key={t} label={t} onClick={() => onPick(t)} testid={`try-chip-${t.toLowerCase().replace(/\s+/g, "-")}`} />)}
    </div>
  </div>
);

const ResultCard = ({ ev }) => (
  <Link
    to={`/?date=${ev.date_dd_mm}&year=${ev.year}`}
    data-testid={`search-result-${ev.id}`}
    className="bg-white border border-[#e5e7eb] rounded-xl p-5 flex gap-4 hover:border-gold transition-colors"
  >
    <img src={`/images/categories/${categorySlug(ev.category)}.png`} alt={ev.category} loading="lazy" className="w-12 h-12 rounded-lg bg-paper shrink-0 hidden sm:block" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/categories/event.png"; }} />
    <div className="text-[11px] font-bold text-gold bg-paper border border-gold/30 rounded-full px-2.5 py-1 h-fit whitespace-nowrap">{ev.year}</div>
    <div className="min-w-0">
      <h4 className="font-semibold text-ink text-[15px]">{ev.event_title}</h4>
      <p className="text-[13px] text-ink-muted mt-1 line-clamp-2">{eventDescription(ev)}</p>
      <p className="text-[10px] uppercase tracking-widest text-[#9ca3af] mt-2">{ev.category} • {ev.date_dd_mm}</p>
    </div>
  </Link>
);

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const query = params.get("search") || "";
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "latest";
  const [input, setInput] = useState(query);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [suggest, setSuggest] = useState({ popular: [], try: [] });

  useEffect(() => { api.get("/events/search/popular").then((r) => setSuggest(r.data)).catch(() => {}); }, []);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v && v !== "all" && v !== "latest" ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  useEffect(() => { setInput(query); setPage(1); }, [query, category, sort]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/events/search", { params: { q: query, category, sort, page, limit: LIMIT } })
      .then((r) => { if (!alive) return; setTotal(r.data.total); setResults((prev) => (page === 1 ? r.data.events : [...prev, ...r.data.events])); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [query, category, sort, page]);

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="px-6 py-8" data-testid="browse-page">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold">Browse The Archive</p>
          <h1 data-testid="browse-title" className="text-[36px] font-bold text-ink mt-2">{query ? `"${query}"` : "Search History"}</h1>

          <div className="mt-6 relative">
            <input
              data-testid="browse-search-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") update({ search: input.trim() }); }}
              placeholder="Search history, birthdays, years..."
              className="w-full h-14 rounded-full border border-[#e5e7eb] bg-white pl-12 pr-6 text-[15px] text-ink placeholder:text-ink-muted focus:border-gold outline-none"
            />
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          </div>

          {!query && <SearchChips suggest={suggest} onPick={(t) => update({ search: t })} />}

          <div className="mt-6 flex flex-wrap gap-2" data-testid="browse-categories">
            {CATEGORIES.map(([slug, label]) => (
              <button
                key={slug}
                data-testid={`browse-cat-${slug}`}
                onClick={() => update({ category: slug })}
                className={`px-4 py-2 rounded-full text-[13px] border transition-colors ${category === slug ? "bg-ink text-white border-ink" : "bg-white border-[#e5e7eb] text-ink-muted hover:border-gold"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex justify-between items-center">
            <p data-testid="browse-count" className="text-[12px] text-[#9ca3af]">{loading && page === 1 ? "Searching..." : `${total.toLocaleString()} events found`}</p>
            <div className="flex gap-2 items-center">
              <span className="text-[11px] uppercase tracking-widest text-[#9ca3af]">Sort</span>
              {[["latest", "Latest"], ["oldest", "Oldest"]].map(([s, label]) => (
                <button key={s} data-testid={`browse-sort-${s}`} onClick={() => update({ sort: s })} className={`px-4 py-1.5 rounded-full text-[12px] border ${sort === s ? "bg-paper border-gold text-ink" : "bg-white border-[#e5e7eb] text-ink-muted hover:border-gold"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid gap-4" data-testid="browse-results">
            {!loading && results.length === 0 ? (
              <div data-testid="browse-empty" className="bg-white border border-[#e5e7eb] rounded-2xl p-12 text-center">
                <h3 className="text-[20px] font-bold text-ink">No events found{query ? ` for "${query}"` : ""}</h3>
                <p className="text-[14px] text-ink-muted mt-2">Try a year (1066, 1969), name (Lincoln), or category (Science). We have 1,000s of searchable events.</p>
                <SearchChips suggest={suggest} onPick={(t) => update({ search: t })} compact />
                <Link to="/" className="inline-block mt-4 bg-ink text-white rounded-full px-6 py-2.5 text-[13px] hover:bg-gold hover:text-ink transition-colors">Explore On This Day →</Link>
              </div>
            ) : results.map((ev) => <ResultCard key={ev.id} ev={ev} />)}
            {loading && <div className="h-20 rounded-xl bg-white/60 animate-pulse" />}
          </div>

          {!loading && results.length < total && (
            <div className="mt-8 text-center">
              <button data-testid="browse-load-more" onClick={() => setPage((p) => p + 1)} className="bg-white border border-[#e5e7eb] hover:border-gold text-ink rounded-full px-6 py-2.5 text-[13px] font-semibold transition-colors">
                Load more ({total - results.length} remaining)
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
